import { repAxios } from './representativeService';

// Servizio del verbale (area associativa): streaming SSE di impaginazione e assistente,
// riscrittura, versioni, dettagli, pubblicazione e PDF. Le chiamate REST passano da repAxios
// (token del rappresentante); lo streaming usa fetch, perché axios nel browser non legge
// la risposta a pezzi.

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const ERRORE_CONNESSIONE = 'Connessione interrotta, riprova';

export interface GestoriSse {
  delta?: (testo: string) => void;
  fine?: (dati: any) => void;
  errore?: (messaggio: string) => void;
  /** Ogni evento con un nome diverso da `delta`/`fine`/`errore` (es. `sezione`, `sezioneFine`). */
  evento?: (nome: string, dati: any) => void;
  /** Lo stream è aperto (risposta 2xx): da qui il server sta lavorando. */
  aperto?: () => void;
  /** Lo stream già aperto si è chiuso senza `fine`/`errore` (chiamato subito prima di `errore`). */
  connessionePersa?: () => void;
  /** Risposta non 2xx: stato e corpo JSON (null se non JSON), chiamato subito prima di `errore`. */
  rifiutato?: (stato: number, corpo: any) => void;
}

export interface VersioneVerbale {
  _id: string;
  motivo: string;
  caratteri: number;
  createdAt: string;
}

export interface ContenutoVersioneVerbale {
  contenuto: string;
  motivo: string;
  createdAt: string;
}

export interface DettagliVerbale {
  contenuto?: string;
  sezioniRiviste?: unknown;
  partecipantiEsterni?: unknown;
  firme?: unknown;
}

const percorsoVerbale = (eventId: string) => `/representative/events/${eventId}/verbale`;

const interrotto = (err: any, segnale?: AbortSignal) => !!segnale?.aborted || err?.name === 'AbortError';

/** Un blocco SSE (`event:`/`data:`, righe `:` = commento) ⇒ { nome, dati } o null se non utile. */
function analizzaBlocco(blocco: string): { nome: string; dati: any } | null {
  let nome = 'message';
  const dati: string[] = [];
  for (const grezza of blocco.split('\n')) {
    const riga = grezza.endsWith('\r') ? grezza.slice(0, -1) : grezza;
    if (!riga || riga.startsWith(':')) continue;
    const dove = riga.indexOf(':');
    const campo = dove < 0 ? riga : riga.slice(0, dove);
    let valore = dove < 0 ? '' : riga.slice(dove + 1);
    if (valore.startsWith(' ')) valore = valore.slice(1);
    if (campo === 'event') nome = valore;
    else if (campo === 'data') dati.push(valore);
  }
  if (!dati.length) return null;
  try {
    return { nome, dati: JSON.parse(dati.join('\n')) };
  } catch {
    return null;
  }
}

/**
 * POST in streaming SSE verso `${API_BASE}${percorso}`: chiama `delta` per ogni pezzo,
 * `fine` con i dati finali, `errore` con il messaggio (risposta non 2xx ⇒ `error` del server,
 * stream chiuso senza `fine` ⇒ «Connessione interrotta, riprova»). Interrotto con `segnale`
 * ⇒ nessuna chiamata. La promessa non si rifiuta mai.
 */
export async function leggiSse(
  percorso: string,
  corpo: unknown,
  gestori: GestoriSse,
  segnale?: AbortSignal
): Promise<void> {
  let risposta: Response;
  try {
    risposta = await fetch(`${API_BASE}${percorso}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('representative_token') || ''}`,
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify(corpo ?? {}),
      signal: segnale,
    });
  } catch (err) {
    if (!interrotto(err, segnale)) gestori.errore?.(ERRORE_CONNESSIONE);
    return;
  }

  if (!risposta.ok) {
    let json: any = null;
    try { json = await risposta.json(); } catch { /* corpo non JSON */ }
    gestori.rifiutato?.(risposta.status, json);
    gestori.errore?.((json && typeof json.error === 'string' && json.error) || 'Errore');
    return;
  }
  if (!risposta.body) {
    gestori.errore?.(ERRORE_CONNESSIONE);
    return;
  }
  gestori.aperto?.();
  const persa = () => {
    gestori.connessionePersa?.();
    gestori.errore?.(ERRORE_CONNESSIONE);
  };

  const lettore = risposta.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let terminato = false;

  const gestisci = (blocco: string) => {
    const evento = analizzaBlocco(blocco);
    if (!evento) return;
    if (evento.nome === 'delta') {
      if (typeof evento.dati?.testo === 'string') gestori.delta?.(evento.dati.testo);
    } else if (evento.nome === 'fine') {
      terminato = true;
      gestori.fine?.(evento.dati);
    } else if (evento.nome === 'errore') {
      terminato = true;
      gestori.errore?.((typeof evento.dati?.messaggio === 'string' && evento.dati.messaggio) || 'Errore');
    } else {
      gestori.evento?.(evento.nome, evento.dati);
    }
  };

  const svuota = () => {
    let fine = buffer.indexOf('\n\n');
    while (fine >= 0 && !terminato) {
      gestisci(buffer.slice(0, fine));
      buffer = buffer.slice(fine + 2);
      fine = buffer.indexOf('\n\n');
    }
  };

  try {
    while (!terminato) {
      const { done, value } = await lettore.read();
      if (done) break;
      buffer = (buffer + decoder.decode(value, { stream: true })).replace(/\r\n/g, '\n');
      svuota();
    }
    if (!terminato) {
      buffer = (buffer + decoder.decode()).replace(/\r\n/g, '\n');
      svuota();
      if (!terminato && buffer.trim()) gestisci(buffer);
    }
  } catch (err) {
    if (interrotto(err, segnale)) return;
    if (!terminato) persa();
    return;
  }

  if (terminato) {
    try { await lettore.cancel(); } catch { /* già chiuso */ }
    return;
  }
  if (!segnale?.aborted) persa();
}

/** Impaginazione AI del testo grezzo: `fine` ⇒ { contenuto, ultimaModifica } (già salvato). */
export const impaginaVerbale = (eventId: string, testo: string, gestori: GestoriSse, segnale?: AbortSignal) =>
  leggiSse(`${percorsoVerbale(eventId)}/impagina`, { testo }, gestori, segnale);

/** Assistente del verbale: `fine` ⇒ { messaggio, proposte }. */
export const chiediAssistente = (eventId: string, messaggio: string, gestori: GestoriSse, segnale?: AbortSignal) =>
  leggiSse(`${percorsoVerbale(eventId)}/assistente`, { messaggio }, gestori, segnale);

/** Riscrittura AI di una parte del verbale (non salva nulla). */
export const riscriviVerbale = async (eventId: string, html: string, istruzione: string): Promise<string> => {
  const { data } = await repAxios.post(`${percorsoVerbale(eventId)}/riscrivi`, { html, istruzione });
  return data.html;
};

export const elencaVersioniVerbale = async (eventId: string): Promise<VersioneVerbale[]> => {
  const { data } = await repAxios.get(`${percorsoVerbale(eventId)}/versioni`);
  return data.versioni || [];
};

export const leggiVersioneVerbale = async (eventId: string, vid: string): Promise<ContenutoVersioneVerbale> => {
  const { data } = await repAxios.get(`${percorsoVerbale(eventId)}/versioni/${vid}`);
  return { contenuto: data.contenuto, motivo: data.motivo, createdAt: data.createdAt };
};

export const ripristinaVersioneVerbale = async (
  eventId: string,
  vid: string
): Promise<{ contenuto: string; ultimaModifica: string }> => {
  const { data } = await repAxios.post(`${percorsoVerbale(eventId)}/versioni/${vid}/ripristina`);
  return { contenuto: data.contenuto, ultimaModifica: data.ultimaModifica };
};

/** Istantanea del verbale salvato (null se il server non l'ha creata). */
export const istantaneaVerbale = async (
  eventId: string,
  motivo: 'manuale' | 'prima_di_applicare'
): Promise<VersioneVerbale | null> => {
  const { data } = await repAxios.post(`${percorsoVerbale(eventId)}/versioni`, { motivo });
  return data.versione || null;
};

/** PUT del verbale con i soli campi passati (senza `contenuto` il testo resta com'è). */
export const salvaDettagliVerbale = async (
  eventId: string,
  dettagli: DettagliVerbale
): Promise<{ ultimaModifica: string | null }> => {
  const { data } = await repAxios.put(percorsoVerbale(eventId), dettagli);
  return { ultimaModifica: data.ultimaModifica ?? null };
};

export const pubblicaVerbale = async (
  eventId: string,
  { notifica }: { notifica: boolean }
): Promise<{ pubblicatoAt: string; notified: number }> => {
  const { data } = await repAxios.post(`${percorsoVerbale(eventId)}/publish`, { notifica });
  return { pubblicatoAt: data.pubblicatoAt, notified: data.notified };
};

/** URL assoluto del PDF (serve il token: per aprirlo usare scaricaPdfVerbale). */
export const urlPdfVerbale = (eventId: string) => `${API_BASE}${percorsoVerbale(eventId)}/pdf`;

export const scaricaPdfVerbale = async (eventId: string): Promise<Blob> => {
  const { data } = await repAxios.get(`${percorsoVerbale(eventId)}/pdf`, { responseType: 'blob' });
  return data;
};

// ---------------------------------------------------------------------------------------------
// Foto dell'evento e post social (piano 3). Solo l'autore: le foto si leggono con il Bearer e
// diventano `blob:` nel sito (niente URL firmati). Gli errori HTTP diventano `ErroreSocial`.
// ---------------------------------------------------------------------------------------------

export interface FotoVerbale {
  _id: string;
  larghezza: number;
  altezza: number;
  dimensione: number;
  caricataAt: string;
}

export interface EsitoCanale {
  id: string;
  tipo: string;
  nome: string;
  esito: 'ok' | 'saltato';
  motivo?: string;
}

export interface StatoSocial {
  disponibile: boolean;
  prova: boolean;
  richiesto: boolean;
  testo: string;
  testoX: string;
  avvisi: string[];
  /**
   * Solo i nomi citati (gli stessi del 409 `CONFERMA_NOMI`): la seconda spunta compare con questi.
   * Assente con un backend di prima.
   */
  avvisiNomi?: string[];
  consensoFotoAt: string | null;
  autorizzatoAt: string | null;
  /** null se il post non è mai stato inviato; `canali` può essere vuoto (bozza ritrovata su PostPickr). */
  postpickr: {
    stato?: 'in_invio' | 'bozza' | 'errore';
    errore?: string;
    /** Data ISO dell'invio. */
    inviatoAt?: string;
    prova?: boolean;
    /** Codice del post su PostPickr (`UNASCA-VERB-<eventId>`, `UNASCA-VERB-PROVA-<eventId>` nelle prove), solo nella risposta dell'invio. */
    codice?: string;
    canali: EsitoCanale[];
  } | null;
}

export interface ErroreSocial {
  codice?: string;
  messaggio: string;
  avvisi?: string[];
  /** Stato HTTP della risposta (assente se la connessione non è arrivata al server). */
  stato?: number;
}

export interface TestiSocialPreparati {
  testo: string;
  testoX: string;
  avvisi: string[];
  /** Nomi citati nei testi preparati (vuoto con un backend di prima). */
  avvisiNomi: string[];
}

const percorsoFoto = (eventId: string) => `${percorsoVerbale(eventId)}/foto`;
const percorsoSocial = (eventId: string) => `${percorsoVerbale(eventId)}/social`;

const soloStringhe = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);

/** Corpo JSON di un errore, anche quando la richiesta chiedeva un Blob (lettura delle foto). */
async function corpoErrore(dati: unknown): Promise<any> {
  if (typeof Blob !== 'undefined' && dati instanceof Blob) {
    try {
      const testo = typeof (dati as any).text === 'function'
        ? await (dati as any).text()
        : await new Promise<string>((ok, ko) => {
          const lettore = new FileReader();
          lettore.onload = () => ok(String(lettore.result || ''));
          lettore.onerror = () => ko(lettore.error);
          lettore.readAsText(dati);
        });
      return JSON.parse(testo);
    } catch {
      return null;
    }
  }
  return dati;
}

/** Errore di axios ⇒ `ErroreSocial` (`code`, `error`, `avvisi` del corpo). */
export async function erroreSocialDa(err: any, predefinito = 'Errore'): Promise<ErroreSocial> {
  const risposta = err?.response;
  if (!risposta) return { messaggio: ERRORE_CONNESSIONE };
  const corpo = await corpoErrore(risposta.data);
  const errore: ErroreSocial = {
    messaggio: (corpo && typeof corpo.error === 'string' && corpo.error) || predefinito,
    stato: risposta.status,
  };
  if (corpo && typeof corpo.code === 'string') errore.codice = corpo.code;
  if (corpo && Array.isArray(corpo.avvisi)) errore.avvisi = soloStringhe(corpo.avvisi);
  return errore;
}

async function conErroreSocial<T>(richiesta: () => Promise<T>, predefinito: string): Promise<T> {
  try {
    return await richiesta();
  } catch (err) {
    throw await erroreSocialDa(err, predefinito);
  }
}

/** Foto dell'evento nell'ordine di caricamento. */
export const elencaFotoVerbale = (eventId: string): Promise<FotoVerbale[]> =>
  conErroreSocial(async () => {
    const { data } = await repAxios.get(percorsoFoto(eventId));
    return Array.isArray(data?.foto) ? data.foto : [];
  }, 'Impossibile leggere le foto');

/** Carica una foto (multipart `file`); `onProgress` riceve la percentuale 0..100. */
export const caricaFotoVerbale = async (
  eventId: string,
  file: File,
  onProgress?: (p: number) => void
): Promise<FotoVerbale> => {
  const MESSAGGIO = 'Caricamento della foto non riuscito';
  const foto = await conErroreSocial(async () => {
    const form = new FormData();
    form.append('file', file);
    const { data } = await repAxios.post(percorsoFoto(eventId), form, {
      onUploadProgress: (e: any) => {
        if (onProgress && e && e.total) onProgress(Math.min(100, Math.round((e.loaded * 100) / e.total)));
      },
    });
    return data?.foto;
  }, MESSAGGIO);
  // Risposta senza la foto (backend diverso o proxy): errore invece di una foto vuota.
  if (!foto || typeof foto._id !== 'string') return Promise.reject<FotoVerbale>({ messaggio: MESSAGGIO } as ErroreSocial);
  return foto;
};

export const eliminaFotoVerbale = (eventId: string, fotoId: string): Promise<void> =>
  conErroreSocial(async () => {
    await repAxios.delete(`${percorsoFoto(eventId)}/${fotoId}`);
  }, 'Eliminazione della foto non riuscita');

/** Una variante della foto come Blob (da mostrare con `URL.createObjectURL`). */
export const leggiFotoVerbale = (
  eventId: string,
  fotoId: string,
  variante: 'anteprima' | 'originale'
): Promise<Blob> =>
  conErroreSocial(async () => {
    const { data } = await repAxios.get(`${percorsoFoto(eventId)}/${fotoId}/${variante}`, { responseType: 'blob' });
    return data;
  }, 'Foto non disponibile');

export const leggiSocialVerbale = (eventId: string): Promise<StatoSocial> =>
  conErroreSocial(async () => {
    const { data } = await repAxios.get(percorsoSocial(eventId));
    return data.social;
  }, 'Impossibile leggere il post social');

/** Salva solo i campi passati (i minimi del testo valgono all'invio). */
export const salvaSocialVerbale = (
  eventId: string,
  campi: { richiesto?: boolean; testo?: string; testoX?: string }
): Promise<StatoSocial> =>
  conErroreSocial(async () => {
    const corpo: { richiesto?: boolean; testo?: string; testoX?: string } = {};
    if (campi.richiesto !== undefined) corpo.richiesto = campi.richiesto;
    if (campi.testo !== undefined) corpo.testo = campi.testo;
    if (campi.testoX !== undefined) corpo.testoX = campi.testoX;
    const { data } = await repAxios.put(percorsoSocial(eventId), corpo);
    return data.social;
  }, 'Salvataggio del post non riuscito');

/** L'assistente scrive il post e la versione per X (già salvati dal server, `richiesto: true`). */
export const preparaSocialVerbale = (eventId: string): Promise<TestiSocialPreparati> =>
  conErroreSocial(async () => {
    const { data } = await repAxios.post(`${percorsoSocial(eventId)}/prepara`);
    return {
      testo: typeof data?.testo === 'string' ? data.testo : '',
      testoX: typeof data?.testoX === 'string' ? data.testoX : '',
      avvisi: soloStringhe(data?.avvisi),
      avvisiNomi: soloStringhe(data?.avvisiNomi),
    };
  }, 'Preparazione del post non riuscita');

/**
 * Invio in bozza su PostPickr. Risponde anche con `stato: 'errore'` (esito da mostrare con «Riprova»);
 * 409 `CONFERMA_NOMI` con `avvisi` ⇒ serve la seconda spunta (`confermaNomi: true`).
 * `fotoConsentite`: gli id delle foto viste alla spunta del consenso (diverse ⇒ 400 `CONSENSO_MANCANTE`).
 */
export const inviaSocialVerbale = (
  eventId: string,
  corpo: { testo: string; testoX: string; consensoFoto: true; fotoConsentite?: string[]; confermaNomi?: boolean }
): Promise<StatoSocial['postpickr']> =>
  conErroreSocial(async () => {
    const { data } = await repAxios.post(`${percorsoSocial(eventId)}/invia`, corpo);
    const postpickr = data?.postpickr ?? null;
    if (postpickr && !Array.isArray(postpickr.canali)) return { ...postpickr, canali: [] };
    return postpickr;
  }, 'Invio del post non riuscito');
